import {EditorState, TextSelection} from "prosemirror-state"
import {EditorView} from "prosemirror-view"
import {Schema} from "prosemirror-model"
import {schema} from "prosemirror-schema-basic"
import {keymap} from "prosemirror-keymap"
import {history, undo, redo} from "prosemirror-history"
import {baseKeymap, toggleMark, setBlockType} from "prosemirror-commands"
import {defaultMarkdownParser, defaultMarkdownSerializer, MarkdownParser, MarkdownSerializer} from "prosemirror-markdown"
import {addListNodes} from "prosemirror-schema-list"

// Create custom schema with image support using the default markdown schema as base
let imageSchema;
let customMarkdownParser;
let customMarkdownSerializer;

// Create a proper image schema using the basic schema with list support and images
try {
    // Start with basic nodes and add list support
    const nodes = addListNodes(schema.spec.nodes, "paragraph block*", "block");
    
    // Add image node
    const nodesWithImage = nodes.addToEnd("image", {
        inline: true,
        attrs: {
            src: {},
            alt: {default: null},
            title: {default: null}
        },
        group: "inline",
        draggable: true,
        parseDOM: [{
            tag: "img[src]",
            getAttrs(dom) {
                return {
                    src: dom.getAttribute("src"),
                    title: dom.getAttribute("title"),
                    alt: dom.getAttribute("alt")
                }
            }
        }],
        toDOM(node) {
            let {src, alt, title} = node.attrs
            return ["img", {src, alt, title, class: "content-image"}]
        }
    });

    imageSchema = new Schema({
        nodes: nodesWithImage,
        marks: schema.spec.marks
    });

    // Create custom parsers with image support
    customMarkdownParser = new MarkdownParser(imageSchema, defaultMarkdownParser.tokenizer, {
        ...defaultMarkdownParser.tokens,
        image: {
            node: "image",
            getAttrs: (tok) => ({
                src: tok.attrGet("src"),
                title: tok.attrGet("title") || null,
                alt: (tok.children && tok.children[0] && tok.children[0].content) || null
            })
        }
    });

    customMarkdownSerializer = new MarkdownSerializer({
        ...defaultMarkdownSerializer.nodes,
        image(state, node) {
            state.write("![" + state.esc(node.attrs.alt || "") + "](" + state.esc(node.attrs.src) + 
                       (node.attrs.title ? " " + state.quote(node.attrs.title) : "") + ")")
        }
    }, defaultMarkdownSerializer.marks);

    console.log('Successfully created image schema with list support');
} catch (error) {
    console.error('Failed to create image schema, falling back to default:', error);
    imageSchema = defaultMarkdownParser.schema;
    customMarkdownParser = defaultMarkdownParser;
    customMarkdownSerializer = defaultMarkdownSerializer;
}

document.addEventListener('DOMContentLoaded', function() {
    const editorElement = document.querySelector("#editor");
    const contentInput = document.querySelector('#content');
    
    if (!editorElement || !contentInput) {
        console.error('Editor elements not found');
        return;
    }

    if (editorElement.querySelector('.ProseMirror')) {
        console.log('Editor already initialized');
        return;
    }
    
    console.log('Initializing editor with image support');

    // Create and add toolbar with image button
    const toolbar = document.createElement('div');
    toolbar.className = 'toolbar';
    toolbar.innerHTML = `
        <button type="button" data-command="undo" title="Undo (Ctrl+Z)">
            <i class="bi bi-arrow-counterclockwise"></i>
        </button>
        <button type="button" data-command="redo" title="Redo (Ctrl+Y)">
            <i class="bi bi-arrow-clockwise"></i>
        </button>
        <span class="toolbar-divider"></span>
        <button type="button" data-command="heading" title="Heading (H3)">
            <i class="bi bi-type-h3"></i>
        </button>
        <button type="button" data-command="strong" title="Bold (Ctrl+B)">
            <i class="bi bi-type-bold"></i>
        </button>
        <button type="button" data-command="em" title="Italic (Ctrl+I)">
            <i class="bi bi-type-italic"></i>
        </button>
        <span class="toolbar-divider"></span>
        <button type="button" data-command="image" title="Insert Image">
            <i class="bi bi-image"></i>
        </button>
    `;

    console.log('Toolbar HTML created');

    // Clear any existing toolbars
    const existingToolbars = editorElement.parentNode.querySelectorAll('.toolbar');
    existingToolbars.forEach(t => t.remove());

    // Add the new toolbar
    editorElement.parentNode.insertBefore(toolbar, editorElement);
    console.log('Toolbar added to DOM');
    
    // Create and setup image modal
    const imageModal = createImageModal();
    setupImageModal();

    const initialContent = contentInput.value || '';
    console.log('Initial content:', initialContent.substring(0, 100));
    
    let doc;
    try {
        doc = initialContent ? 
            customMarkdownParser.parse(initialContent) : 
            imageSchema.node("doc", null, [imageSchema.node("paragraph")]);
        console.log('Document parsed successfully');
    } catch (error) {
        console.error('Error parsing document:', error);
        doc = imageSchema.node("doc", null, [imageSchema.node("paragraph")]);
    }

    let view;
    try {
        view = new EditorView(editorElement, {
            state: EditorState.create({
                doc: doc,
                schema: imageSchema,
                plugins: [
                    history(),
                    keymap({
                        "Mod-z": undo,
                        "Mod-y": redo,
                        "Mod-Shift-z": redo,
                        "Mod-b": toggleMark(imageSchema.marks.strong),
                        "Mod-i": toggleMark(imageSchema.marks.em)
                    }),
                    keymap(baseKeymap)
                ]
            }),
            dispatchTransaction: function(transaction) {
                const newState = view.state.apply(transaction);
                view.updateState(newState);
                const serialized = customMarkdownSerializer.serialize(newState.doc);
                
                // Check for base64 images and warn user
                if (serialized.includes('data:image/')) {
                    console.warn('Base64 images detected in content');
                    // Show a one-time warning
                    if (!window.base64Warning) {
                        window.base64Warning = true;
                        setTimeout(() => {
                            alert('Warning: Large embedded images detected. For better performance, please use the image upload button instead of pasting images directly.');
                        }, 100);
                    }
                }
                
                contentInput.value = serialized;
            }
        });
        console.log('Editor view created successfully');
    } catch (error) {
        console.error('Error creating editor view:', error);
        return;
    }

    // Image modal functionality
    let currentImageData = null;
    
    function createImageModal() {
        const modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.id = 'imageModal';
        modal.innerHTML = `
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="bi bi-image"></i> Insert Image
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6">
                                <h6>Upload Image</h6>
                                <div class="upload-area" id="uploadArea">
                                    <div class="upload-placeholder">
                                        <i class="bi bi-cloud-upload display-4 text-muted"></i>
                                        <p class="text-muted">Drag & drop an image here or click to browse</p>
                                        <button type="button" class="btn btn-outline-primary" id="browseBtn">
                                            <i class="bi bi-folder2-open"></i> Browse Files
                                        </button>
                                    </div>
                                    <input type="file" id="imageFileInput" accept="image/*" style="display: none;">
                                </div>
                                
                                <div class="mt-3">
                                    <h6>Or Insert from URL</h6>
                                    <div class="input-group">
                                        <input type="url" class="form-control" id="imageUrlInput" placeholder="https://example.com/image.jpg">
                                        <button class="btn btn-outline-secondary" type="button" id="loadUrlBtn">
                                            <i class="bi bi-link-45deg"></i> Load
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="col-md-6">
                                <h6>Preview & Edit</h6>
                                <div class="image-preview-container">
                                    <div id="imagePreview" class="image-preview">
                                        <div class="preview-placeholder">
                                            <i class="bi bi-image display-4 text-muted"></i>
                                            <p class="text-muted">Image preview will appear here</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="image-controls mt-3" id="imageControls" style="display: none;">
                                    <div class="mb-3">
                                        <label class="form-label small">Crop Image</label>
                                        <div class="crop-container" id="cropContainer" style="display: none;">
                                            <canvas id="cropCanvas" style="max-width: 100%; border: 1px solid #ddd; cursor: crosshair;"></canvas>
                                            <div class="mt-2">
                                                <button type="button" class="btn btn-sm btn-outline-primary" id="applyCropBtn">Apply Crop</button>
                                                <button type="button" class="btn btn-sm btn-outline-secondary" id="resetCropBtn">Reset</button>
                                            </div>
                                        </div>
                                        <button type="button" class="btn btn-sm btn-outline-success" id="enableCropBtn">
                                            <i class="bi bi-crop"></i> Enable Cropping
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="row mt-3">
                            <div class="col-12">
                                <h6>Image Details</h6>
                                <div class="row g-2">
                                    <div class="col-md-6">
                                        <label for="altText" class="form-label">Alt Text <small class="text-muted">(optional)</small></label>
                                        <input type="text" class="form-control" id="altText" placeholder="Describe the image (optional)">
                                    </div>
                                    <div class="col-md-6">
                                        <label for="imageTitle" class="form-label">Title <small class="text-muted">(optional - will appear below image)</small></label>
                                        <input type="text" class="form-control" id="imageTitle" placeholder="Image caption/title">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="insertImageBtn" disabled>
                            <i class="bi bi-plus-circle"></i> Insert Image
                        </button>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        return modal;
    }
    
    function insertImageMarkdown(src, alt = '', title = '') {
        const {state} = view;
        const {selection} = state;
        
        // Build the image markdown
        let imageMarkdown = `![${alt || ''}](${src}`;
        if (title) {
            imageMarkdown += ` "${title}"`;
        }
        imageMarkdown += ')';
        
        // Add title below image if provided, and add new lines
        if (title) {
            imageMarkdown += `\n\n*${title}*\n\n`;
        } else {
            imageMarkdown += '\n\n';
        }
        
        // Try to create an image node if the schema supports it
        if (imageSchema.nodes.image) {
            try {
                console.log('Creating image node with title:', title);
                const imageNode = imageSchema.nodes.image.create({
                    src: src,
                    alt: alt || '',
                    title: title || null
                });
                
                // Create all content at once
                let content = [];
                
                // 1. Image paragraph
                const imageParagraph = imageSchema.nodes.paragraph.create(null, [imageNode]);
                content.push(imageParagraph);
                
                // 2. Title paragraph (if title exists)
                if (title) {
                    const titleParagraph = imageSchema.nodes.paragraph.create(null, [
                        imageSchema.text(title, [imageSchema.marks.em.create()])
                    ]);
                    content.push(titleParagraph);
                }
                
                // 3. Cursor paragraph
                const cursorParagraph = imageSchema.nodes.paragraph.create();
                content.push(cursorParagraph);
                
                // Create fragment and insert all at once
                const fragment = imageSchema.nodes.doc.create(null, content).content;
                
                let tr = state.tr;
                const $from = selection.$from;
                
                // Replace current selection/paragraph with all content
                if ($from.parent.type.name === 'paragraph' && $from.parent.textContent === '') {
                    // Replace empty paragraph
                    tr = tr.replaceRangeWith($from.before(), $from.after(), fragment);
                } else {
                    // Replace selection
                    tr = tr.replaceSelection(fragment);
                }
                
                // Set cursor in the last (cursor) paragraph
                const cursorPos = title ? tr.selection.to - 1 : tr.selection.to - 1;
                const newPos = tr.doc.resolve(cursorPos);
                tr = tr.setSelection(new TextSelection(newPos));
                
                view.dispatch(tr);
                view.focus();
                console.log('Inserted image node with title successfully');
                return;
            } catch (error) {
                console.error('Failed to insert image node:', error);
            }
        }
        
        // Fallback to markdown text insertion
        const transaction = state.tr.insertText(imageMarkdown, selection.from, selection.to);
        view.dispatch(transaction);
        view.focus();
        console.log('Inserted image as markdown text');
    }
    
    function processImage(file, quality = 0.8, maxSize = 100) {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = function() {
                // Calculate new dimensions
                const scaleFactor = maxSize / 100;
                canvas.width = img.width * scaleFactor;
                canvas.height = img.height * scaleFactor;
                
                // Draw and compress
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
                resolve(compressedDataUrl);
            };
            
            const reader = new FileReader();
            reader.onload = (e) => img.src = e.target.result;
            reader.readAsDataURL(file);
        });
    }
    
    function setupImageModal() {
        const modal = document.getElementById('imageModal');
        const uploadArea = document.getElementById('uploadArea');
        const browseBtn = document.getElementById('browseBtn');
        const fileInput = document.getElementById('imageFileInput');
        const urlInput = document.getElementById('imageUrlInput');
        const loadUrlBtn = document.getElementById('loadUrlBtn');
        const imagePreview = document.getElementById('imagePreview');
        const imageControls = document.getElementById('imageControls');
        const altText = document.getElementById('altText');
        const imageTitle = document.getElementById('imageTitle');
        const insertBtn = document.getElementById('insertImageBtn');
        
        // Cropping elements
        const enableCropBtn = document.getElementById('enableCropBtn');
        const cropContainer = document.getElementById('cropContainer');
        const cropCanvas = document.getElementById('cropCanvas');
        const applyCropBtn = document.getElementById('applyCropBtn');
        const resetCropBtn = document.getElementById('resetCropBtn');
        
        let originalImageData = null;
        let cropSelection = null;
        let isDragging = false;
        
        // File upload handlers
        browseBtn.addEventListener('click', () => fileInput.click());
        
        uploadArea.addEventListener('dragover', (e) => {
            e.preventDefault();
            uploadArea.classList.add('drag-over');
        });
        
        uploadArea.addEventListener('dragleave', () => {
            uploadArea.classList.remove('drag-over');
        });
        
        uploadArea.addEventListener('drop', (e) => {
            e.preventDefault();
            uploadArea.classList.remove('drag-over');
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                handleFileUpload(files[0]);
            }
        });
        
        fileInput.addEventListener('change', (e) => {
            if (e.target.files.length > 0) {
                handleFileUpload(e.target.files[0]);
            }
        });
        
        // URL loading
        loadUrlBtn.addEventListener('click', () => {
            const url = urlInput.value.trim();
            if (url) {
                loadImageFromUrl(url);
            }
        });
        
        urlInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                const url = urlInput.value.trim();
                if (url) {
                    loadImageFromUrl(url);
                }
            }
        });
        
        // Cropping event handlers
        enableCropBtn.addEventListener('click', () => {
            if (originalImageData) {
                enableCropping();
            }
        });
        
        applyCropBtn.addEventListener('click', () => {
            if (cropSelection) {
                applyCrop();
            }
        });
        
        resetCropBtn.addEventListener('click', () => {
            resetCrop();
        });
        
        // Canvas mouse events for cropping
        cropCanvas.addEventListener('mousedown', startCrop);
        cropCanvas.addEventListener('mousemove', updateCrop);
        cropCanvas.addEventListener('mouseup', endCrop);
        
        // Insert button
        insertBtn.addEventListener('click', () => {
            if (currentImageData) {
                const alt = altText.value || '';
                const title = imageTitle.value || '';
                
                // If it's a file that hasn't been uploaded yet, upload it first
                if (currentImageData.type === 'file') {
                    uploadImageToServer(currentImageData.file).then(() => {
                        insertImageMarkdown(currentImageData.src, alt, title);
                        bootstrap.Modal.getInstance(modal).hide();
                        resetModal();
                    });
                } else {
                    insertImageMarkdown(currentImageData.src, alt, title);
                    bootstrap.Modal.getInstance(modal).hide();
                    resetModal();
                }
            }
        });
        
        // Reset modal when closed
        modal.addEventListener('hidden.bs.modal', resetModal);
        
        function handleFileUpload(file) {
            // Validate file
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file.');
                return;
            }
            
            if (file.size > 5 * 1024 * 1024) {
                alert('Image file is too large. Please select a file smaller than 5MB.');
                return;
            }
            
            // Store original file for cropping
            originalImageData = file;
            
            // Show preview for cropping
            const reader = new FileReader();
            reader.onload = (e) => {
                imagePreview.innerHTML = `<img src="${e.target.result}" class="img-fluid" alt="Preview">`;
                imageControls.style.display = 'block';
                insertBtn.disabled = false;
                
                // Set default alt text
                if (!altText.value) {
                    altText.value = file.name.replace(/\.[^/.]+$/, "");
                }
                
                // Store current image data
                currentImageData = { src: e.target.result, type: 'file', file: file };
            };
            reader.readAsDataURL(file);
        }

        function uploadImageToServer(file, croppedBlob = null) {
            const formData = new FormData();
            formData.append('image', croppedBlob || file);

            // Show loading state
            imagePreview.innerHTML = `
                <div class="preview-placeholder">
                    <i class="bi bi-hourglass-split display-4 text-muted"></i>
                    <p class="text-muted">Uploading image...</p>
                </div>
            `;
            imageControls.style.display = 'none';
            insertBtn.disabled = true;

            return fetch('/contribution/uploadEditorImage', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    currentImageData = { src: data.url, type: 'server' };
                    
                    // Show the uploaded image
                    imagePreview.innerHTML = `<img src="${data.url}" class="img-fluid" alt="Preview">`;
                    
                    // Set default alt text from filename (don't auto-fill title)
                    if (!altText.value) {
                        altText.value = file.name.replace(/\.[^/.]+$/, "");
                    }
                    
                    // Show controls for cropping
                    imageControls.style.display = 'block';
                    insertBtn.disabled = false;
                    
                    return data;
                } else {
                    throw new Error(data.error || 'Upload failed');
                }
            })
            .catch(error => {
                console.error('Upload error:', error);
                alert('Failed to upload image: ' + error.message);
                
                // Reset to placeholder
                resetModal();
                throw error;
            });
        }
        
        function loadImageFromUrl(url) {
            try {
                new URL(url);
            } catch (e) {
                alert('Please enter a valid URL.');
                return;
            }
            
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function() {
                currentImageData = { src: url, type: 'url' };
                imagePreview.innerHTML = `<img src="${url}" class="img-fluid" alt="Preview">`;
                imageControls.style.display = 'none'; // No editing for URL images
                insertBtn.disabled = false;
                
                // Set default alt text from URL
                const filename = url.split('/').pop().split('?')[0];
                altText.value = filename.replace(/\.[^/.]+$/, "");
            };
            img.onerror = function() {
                alert('Failed to load image from URL. Please check the URL and try again.');
            };
            img.src = url;
        }
        
        async function updateImagePreview() {
            if (!currentImageData || !currentImageData.file) return;
            
            const quality = qualitySlider.value / 100;
            const size = parseInt(document.querySelector('[data-size].active').dataset.size);
            
            const processedSrc = await processImage(currentImageData.file, quality, size);
            currentImageData.src = processedSrc;
            
            imagePreview.innerHTML = `<img src="${processedSrc}" class="img-fluid" alt="Preview">`;
            imageControls.style.display = 'block';
            insertBtn.disabled = false;
        }
        
        function enableCropping() {
            const img = imagePreview.querySelector('img');
            if (!img) return;
            
            // Set up canvas
            const rect = img.getBoundingClientRect();
            cropCanvas.width = img.naturalWidth;
            cropCanvas.height = img.naturalHeight;
            cropCanvas.style.width = img.offsetWidth + 'px';
            cropCanvas.style.height = img.offsetHeight + 'px';
            
            const ctx = cropCanvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            
            // Show crop interface
            cropContainer.style.display = 'block';
            enableCropBtn.style.display = 'none';
            imagePreview.style.display = 'none';
        }
        
        function startCrop(e) {
            isDragging = true;
            const rect = cropCanvas.getBoundingClientRect();
            const scaleX = cropCanvas.width / rect.width;
            const scaleY = cropCanvas.height / rect.height;
            
            cropSelection = {
                startX: (e.clientX - rect.left) * scaleX,
                startY: (e.clientY - rect.top) * scaleY,
                endX: (e.clientX - rect.left) * scaleX,
                endY: (e.clientY - rect.top) * scaleY
            };
        }
        
        function updateCrop(e) {
            if (!isDragging || !cropSelection) return;
            
            const rect = cropCanvas.getBoundingClientRect();
            const scaleX = cropCanvas.width / rect.width;
            const scaleY = cropCanvas.height / rect.height;
            
            cropSelection.endX = (e.clientX - rect.left) * scaleX;
            cropSelection.endY = (e.clientY - rect.top) * scaleY;
            
            drawCropSelection();
        }
        
        function endCrop() {
            isDragging = false;
        }
        
        function drawCropSelection() {
            if (!cropSelection) return;
            
            const ctx = cropCanvas.getContext('2d');
            const img = imagePreview.querySelector('img');
            
            // Redraw original image
            ctx.clearRect(0, 0, cropCanvas.width, cropCanvas.height);
            ctx.drawImage(img, 0, 0);
            
            // Draw selection rectangle
            ctx.strokeStyle = '#007bff';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            
            const x = Math.min(cropSelection.startX, cropSelection.endX);
            const y = Math.min(cropSelection.startY, cropSelection.endY);
            const width = Math.abs(cropSelection.endX - cropSelection.startX);
            const height = Math.abs(cropSelection.endY - cropSelection.startY);
            
            ctx.strokeRect(x, y, width, height);
            
            // Draw overlay
            ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
            ctx.fillRect(0, 0, cropCanvas.width, y);
            ctx.fillRect(0, y + height, cropCanvas.width, cropCanvas.height - y - height);
            ctx.fillRect(0, y, x, height);
            ctx.fillRect(x + width, y, cropCanvas.width - x - width, height);
        }
        
        function applyCrop() {
            if (!cropSelection) return;
            
            const x = Math.min(cropSelection.startX, cropSelection.endX);
            const y = Math.min(cropSelection.startY, cropSelection.endY);
            const width = Math.abs(cropSelection.endX - cropSelection.startX);
            const height = Math.abs(cropSelection.endY - cropSelection.startY);
            
            if (width < 10 || height < 10) {
                alert('Crop area is too small');
                return;
            }
            
            // Create cropped canvas
            const croppedCanvas = document.createElement('canvas');
            croppedCanvas.width = width;
            croppedCanvas.height = height;
            const croppedCtx = croppedCanvas.getContext('2d');
            
            const img = imagePreview.querySelector('img');
            croppedCtx.drawImage(img, x, y, width, height, 0, 0, width, height);
            
            // Convert to blob and upload
            croppedCanvas.toBlob((blob) => {
                uploadImageToServer(originalImageData, blob);
                resetCrop();
            }, 'image/jpeg', 0.85);
        }
        
        function resetCrop() {
            cropContainer.style.display = 'none';
            enableCropBtn.style.display = 'block';
            imagePreview.style.display = 'block';
            cropSelection = null;
        }
        
        function resetModal() {
            currentImageData = null;
            originalImageData = null;
            cropSelection = null;
            
            imagePreview.innerHTML = `
                <div class="preview-placeholder">
                    <i class="bi bi-image display-4 text-muted"></i>
                    <p class="text-muted">Image preview will appear here</p>
                </div>
            `;
            imagePreview.style.display = 'block';
            imageControls.style.display = 'none';
            cropContainer.style.display = 'none';
            enableCropBtn.style.display = 'block';
            
            insertBtn.disabled = true;
            altText.value = '';
            imageTitle.value = '';
            urlInput.value = '';
            fileInput.value = '';
        }
    }

    toolbar.addEventListener('click', function(e) {
        const button = e.target.closest('button');
        if (!button) return;
        
        const command = button.dataset.command;
        switch (command) {
            case 'heading':
                const $from = view.state.selection.$from;
                
                const toggleHeading = (state, dispatch) => {
                    const isHeading = $from.parent.type === state.schema.nodes.heading;
                    
                    if (isHeading) {
                        return setBlockType(state.schema.nodes.paragraph)(state, dispatch);
                    } else {
                        return setBlockType(state.schema.nodes.heading, { level: 3 })(state, dispatch);
                    }
                };
                
                toggleHeading(view.state, view.dispatch);
                break;
                
            case 'undo':
                undo(view.state, view.dispatch);
                break;
            case 'redo':
                redo(view.state, view.dispatch);
                break;
            case 'strong':
                toggleMark(imageSchema.marks.strong)(view.state, view.dispatch);
                break;
            case 'em':
                toggleMark(imageSchema.marks.em)(view.state, view.dispatch);
                break;
            case 'image':
                // Open image modal
                const modal = new bootstrap.Modal(document.getElementById('imageModal'));
                modal.show();
                break;
        }
        view.focus();
    });
}); 